import React, { useEffect, useMemo, useState } from 'react';
import { Select, SelectProps } from 'antd';
import { useDispatch, useSelector } from 'umi';
import * as LocationSelectors from '@/selectors/locationSelectors';
import * as PreferencesSelectors from '@/selectors/preferencesSelectors';

export interface TimeZoneSelectorProps extends Omit<SelectProps, 'options' | 'onChange' | 'value'> {
  /**
   * Controlled value for timezone. If not provided, uses Redux state.
   */
  value?: string;
  /**
   * Callback when timezone changes. If not provided, dispatches Redux action.
   */
  onChange?: (value: string) => void;
  /**
   * Whether to show search functionality
   * @default true
   */
  showSearch?: boolean;
  /**
   * Placeholder text
   * @default "Select timezone"
   */
  placeholder?: string;
  /**
   * Minimum width for the selector
   * @default 260
   */
  minWidth?: number;
  /**
   * Hide the label when the dropdown is not active (closed), showing only the prefix
   * @default false
   */
  hideLabelWhenInactive?: boolean;
}

/**
 * TimeZoneSelector - A standalone reusable component for timezone selection
 *
 * Can be used in two modes:
 * 1. Controlled mode: Pass value and onChange props for custom handling
 * 2. Redux mode: Don't pass value/onChange, component will use Redux state automatically
 *
 * @example
 * // Redux mode (automatic)
 * <TimeZoneSelector />
 *
 * @example
 * // Controlled mode
 * <TimeZoneSelector value={timezone} onChange={handleChange} />
 */
const TimeZoneSelector: React.FC<TimeZoneSelectorProps> = ({
  value,
  onChange,
  showSearch = true,
  placeholder = 'Select timezone',
  minWidth = 260,
  hideLabelWhenInactive = false,
  style,
  ...restProps
}) => {
  const dispatch = useDispatch();
  const timezones: string[] = useSelector(LocationSelectors.selectTimezones) as unknown as string[];
  const isPreferencesInitialized = useSelector(PreferencesSelectors.isPreferencesListInitialized);
  const reduxTimezone = useSelector(PreferencesSelectors.selectSelectedTimezone);
  const [isOpen, setIsOpen] = useState(false);

  // Determine if component is in controlled mode
  const isControlled = value !== undefined && onChange !== undefined;
  const currentValue = isControlled ? value : reduxTimezone;

  useEffect(() => {
    // Fetch preferences if not initialized
    if (!isPreferencesInitialized) {
      dispatch({
        type: 'preferences/fetch',
      });
    }
    // Fetch timezones if not loaded
    if (!timezones || timezones.length === 0) {
      dispatch({ type: 'location/getTimeZones' });
    }
  }, [dispatch, isPreferencesInitialized, timezones]);

  const timezoneOptions = useMemo(
    () => (timezones || []).map(tz => ({ label: tz, value: tz })),
    [timezones]
  );

  const handleTimezoneChange = (selectedValue?: string) => {
    if (!selectedValue) return;

    if (isControlled && onChange) {
      // Controlled mode: call the provided onChange
      onChange(selectedValue);
    } else {
      // Redux mode: dispatch to store
      dispatch({
        type: 'preferences/update',
        payload: {
          key: 'timezone',
          value: {
            value: selectedValue,
          },
        },
      });
    }
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    setIsOpen(open);
    // Call the original onDropdownVisibleChange if provided
    if (restProps.onDropdownVisibleChange) {
      restProps.onDropdownVisibleChange(open);
    }
  };

  const renderLabel = (props: any) => {
    // If hideLabelWhenInactive is true and dropdown is closed, show only prefix
    if (hideLabelWhenInactive && !isOpen && restProps.prefix) {
      return " ";
    }
    // Otherwise, show the default label (prefix + value)
    return props.label;
  };

  const getMinWidth = () => {
    if (hideLabelWhenInactive && !isOpen && restProps.prefix) {
      return 20;
    }
    return minWidth || style?.minWidth
  }
  return (
    <Select
      showSearch={showSearch}
      placeholder={placeholder}
      value={currentValue}
      options={timezoneOptions}
      onChange={handleTimezoneChange}
      optionFilterProp="label"
      style={{ ...style, minWidth: getMinWidth() }}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      labelRender={hideLabelWhenInactive ? renderLabel : undefined}
      {...restProps}
    />
  );
};

export default TimeZoneSelector;
